import React, { useState, useCallback } from 'react';
import {
  Card,
  Select,
  Button,
  Table,
  Tag,
  message,
  Spin,
  Space,
  Pagination,
  Checkbox,
  Tooltip,
  Modal,
  Descriptions,
  Badge,
  Progress,
  Alert
} from 'antd';
import {
  SearchOutlined,
  SyncOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  LinkOutlined,
  PlusOutlined,
  ReloadOutlined,
  DatabaseOutlined,
  CloudDownloadOutlined
} from '@ant-design/icons';
import {
  fetchRACityListings,
  compareRAEvents,
  scrapeRAEvent,
  RA_CITIES,
  RA_COUNTRIES
} from '../../server/scraper/raScraperServer';

const { Option } = Select;

// Mapping from RA country codes to database country names
const RA_TO_DB_COUNTRY = {
  'ca': 'Canada',
  'us': 'United States',
  'uk': 'United Kingdom',
  'de': 'Germany',
  'es': 'Spain',
  'nl': 'Netherlands',
  'fr': 'France',
};

// Mapping from RA city slugs to database city names
const RA_TO_DB_CITY = {
  'montreal': 'Montreal',
  'toronto': 'Toronto',
  'vancouver': 'Vancouver',
  'calgary': 'Calgary',
  'ottawa': 'Ottawa',
  'newyork': 'New York',
  'losangeles': 'Los Angeles',
  'chicago': 'Chicago',
  'miami': 'Miami',
  'sanfrancisco': 'San Francisco',
  'detroit': 'Detroit',
  'seattle': 'Seattle',
  'london': 'London',
  'manchester': 'Manchester',
  'bristol': 'Bristol',
  'glasgow': 'Glasgow',
  'leeds': 'Leeds',
  'berlin': 'Berlin',
  'munich': 'Munich',
  'frankfurt': 'Frankfurt',
  'barcelona': 'Barcelona',
  'madrid': 'Madrid',
  'ibiza': 'Ibiza',
  'amsterdam': 'Amsterdam',
  'paris': 'Paris',
};

const RAComparatorTab = ({ onScrapeEvent, countries = [] }) => {
  // State for filters
  const [selectedCountry, setSelectedCountry] = useState('ca');
  const [selectedCity, setSelectedCity] = useState('montreal');
  const [currentPage, setCurrentPage] = useState(1);

  // State for data
  const [loading, setLoading] = useState(false);
  const [comparing, setComparing] = useState(false);
  const [raEvents, setRaEvents] = useState([]);
  const [comparisonResult, setComparisonResult] = useState(null);
  const [selectedEvents, setSelectedEvents] = useState([]);

  // State for scraping
  const [scrapingEvent, setScrapingEvent] = useState(null);
  const [scrapingProgress, setScrapingProgress] = useState({ current: 0, total: 0 });
  const [batchScraping, setBatchScraping] = useState(false);

  // State for event details modal
  const [detailsModalVisible, setDetailsModalVisible] = useState(false);
  const [eventDetails, setEventDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);

  // Get cities based on selected country
  const availableCities = RA_CITIES[selectedCountry] || [];

  // Handle country change
  const handleCountryChange = (country) => {
    setSelectedCountry(country);
    const cities = RA_CITIES[country] || [];
    setSelectedCity(cities[0]?.value || '');
    setRaEvents([]);
    setComparisonResult(null);
    setSelectedEvents([]);
  };

  // Fetch RA listings
  const handleFetchListings = async () => {
    if (!selectedCity) {
      message.warning('Please select a city');
      return;
    }

    try {
      setLoading(true);
      setComparisonResult(null);
      setSelectedEvents([]);

      const result = await fetchRACityListings({
        city: selectedCity,
        page: currentPage,
        country: selectedCountry
      });

      setRaEvents(result.events || []);
      message.success(`Fetched ${result.totalEvents || 0} events from RA.co`);
    } catch (error) {
      const errorMsg = error.response?.data?.message || error.message || 'Failed to fetch RA listings';
      message.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  // Compare with database
  const handleCompare = async () => {
    if (raEvents.length === 0) {
      message.warning('No events to compare. Fetch RA listings first.');
      return;
    }

    try {
      setComparing(true);

      const result = await compareRAEvents(raEvents);
      setComparisonResult(result.data);

      // Auto-select missing events
      const missingIds = (result.data?.missingEvents || []).map(e => e.raEventId);
      setSelectedEvents(missingIds);

      message.success(`Comparison complete: ${result.data?.missing || 0} missing events`);
    } catch (error) {
      const errorMsg = error.response?.data?.message || error.message || 'Failed to compare events';
      message.error(errorMsg);
    } finally {
      setComparing(false);
    }
  };

  // View event details
  const handleViewDetails = async (event) => {
    try {
      setLoadingDetails(true);
      setDetailsModalVisible(true);

      const details = await scrapeRAEvent(event.url);
      setEventDetails(details);
    } catch (error) {
      message.error('Failed to fetch event details');
    } finally {
      setLoadingDetails(false);
    }
  };

  // Get database country and city IDs from RA selection
  const getDbLocationIds = () => {
    const dbCountryName = RA_TO_DB_COUNTRY[selectedCountry];
    const dbCityName = RA_TO_DB_CITY[selectedCity];

    let countryId = null;
    let cityId = null;

    if (dbCountryName && countries.length > 0) {
      const foundCountry = countries.find(c =>
        c.name?.toLowerCase() === dbCountryName.toLowerCase()
      );

      if (foundCountry) {
        countryId = foundCountry._id;

        if (dbCityName && foundCountry.cities) {
          const foundCity = foundCountry.cities.find(city =>
            city.name?.toLowerCase() === dbCityName.toLowerCase()
          );
          if (foundCity) {
            cityId = foundCity._id;
          }
        }
      }
    }

    return { countryId, cityId };
  };

  // Scrape single event
  const handleScrapeEvent = async (event) => {
    try {
      setScrapingEvent(event.raEventId);

      if (onScrapeEvent) {
        // Pass city info and raEventId to the scrape handler
        const cityInfo = getDbLocationIds();
        await onScrapeEvent(event.url, {
          ...cityInfo,
          raEventId: event.raEventId
        });
        message.success(`Event "${event.title}" ready for creation`);
      } else {
        // Open in new tab if no callback
        window.open(event.url, '_blank');
      }
    } catch (error) {
      message.error('Failed to scrape event');
    } finally {
      setScrapingEvent(null);
    }
  };

  // Batch scrape selected events
  const handleBatchScrape = async () => {
    if (selectedEvents.length === 0) {
      message.warning('No events selected');
      return;
    }

    const eventsToScrape = comparisonResult?.missingEvents?.filter(
      e => selectedEvents.includes(e.raEventId)
    ) || [];

    if (eventsToScrape.length === 0) {
      message.warning('No missing events selected');
      return;
    }

    setBatchScraping(true);
    setScrapingProgress({ current: 0, total: eventsToScrape.length });

    for (let i = 0; i < eventsToScrape.length; i++) {
      const event = eventsToScrape[i];
      setScrapingProgress({ current: i + 1, total: eventsToScrape.length });

      try {
        if (onScrapeEvent) {
          await onScrapeEvent(event.url);
        }
        // Add delay to avoid rate limiting
        await new Promise(resolve => setTimeout(resolve, 2000));
      } catch (error) {
        message.error(`Failed to scrape: ${event.title}`);
      }
    }

    setBatchScraping(false);
    message.success(`Batch scrape complete: ${eventsToScrape.length} events processed`);
  };

  // Table columns for RA events
  const columns = [
    {
      title: (
        <Checkbox
          checked={selectedEvents.length === (comparisonResult?.missingEvents?.length || 0) && selectedEvents.length > 0}
          indeterminate={selectedEvents.length > 0 && selectedEvents.length < (comparisonResult?.missingEvents?.length || 0)}
          onChange={(e) => {
            if (e.target.checked) {
              setSelectedEvents((comparisonResult?.missingEvents || []).map(e => e.raEventId));
            } else {
              setSelectedEvents([]);
            }
          }}
          disabled={!comparisonResult}
        />
      ),
      dataIndex: 'select',
      key: 'select',
      width: 50,
      render: (_, record) => {
        const isMissing = comparisonResult?.missingEvents?.some(e => e.raEventId === record.raEventId);
        return (
          <Checkbox
            checked={selectedEvents.includes(record.raEventId)}
            onChange={(e) => {
              if (e.target.checked) {
                setSelectedEvents([...selectedEvents, record.raEventId]);
              } else {
                setSelectedEvents(selectedEvents.filter(id => id !== record.raEventId));
              }
            }}
            disabled={!isMissing}
          />
        );
      }
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (_, record) => {
        if (!comparisonResult) {
          return <Tag color="default">Unknown</Tag>;
        }
        const isInDB = comparisonResult.existingEvents?.some(e => e.raEventId === record.raEventId);
        return isInDB ? (
          <Tag icon={<CheckCircleOutlined />} color="success">In DB</Tag>
        ) : (
          <Tag icon={<ExclamationCircleOutlined />} color="warning">Missing</Tag>
        );
      }
    },
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
      ellipsis: true,
      render: (text, record) => (
        <Tooltip title={text}>
          <a onClick={() => handleViewDetails(record)} style={{ cursor: 'pointer' }}>
            {text}
          </a>
        </Tooltip>
      )
    },
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
      width: 120,
    },
    {
      title: 'RA ID',
      dataIndex: 'raEventId',
      key: 'raEventId',
      width: 100,
      render: (id) => <code>{id}</code>
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 180,
      render: (_, record) => {
        const isMissing = !comparisonResult || comparisonResult.missingEvents?.some(e => e.raEventId === record.raEventId);
        return (
          <Space>
            <Tooltip title="View on RA">
              <Button
                icon={<LinkOutlined />}
                size="small"
                onClick={() => window.open(record.url, '_blank')}
              />
            </Tooltip>
            <Tooltip title="Scrape & Add">
              <Button
                type="primary"
                icon={<PlusOutlined />}
                size="small"
                loading={scrapingEvent === record.raEventId}
                disabled={!isMissing}
                onClick={() => handleScrapeEvent(record)}
              >
                Add
              </Button>
            </Tooltip>
          </Space>
        );
      }
    },
  ];

  return (
    <div className="space-y-6">
      {/* Filters Card */}
      <Card
        title={
          <div className="flex items-center gap-2">
            <SearchOutlined className="text-blue-500" />
            <span>RA Events Comparator</span>
          </div>
        }
        className="shadow-lg"
      >
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
          {/* Country Select */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
            <Select
              value={selectedCountry}
              onChange={handleCountryChange}
              style={{ width: '100%' }}
              size="large"
            >
              {RA_COUNTRIES.map(country => (
                <Option key={country.value} value={country.value}>
                  {country.label}
                </Option>
              ))}
            </Select>
          </div>

          {/* City Select */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
            <Select
              value={selectedCity}
              onChange={setSelectedCity}
              style={{ width: '100%' }}
              size="large"
              placeholder="Select a city"
            >
              {availableCities.map(city => (
                <Option key={city.value} value={city.value}>
                  {city.label}
                </Option>
              ))}
            </Select>
          </div>

          {/* Page Select */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Page (Week)</label>
            <Select
              value={currentPage}
              onChange={setCurrentPage}
              style={{ width: '100%' }}
              size="large"
            >
              {Array.from({ length: 20 }, (_, i) => i + 1).map(page => (
                <Option key={page} value={page}>
                  Page {page} {page === 1 ? '(This week)' : `(Week ${page})`}
                </Option>
              ))}
            </Select>
          </div>

          {/* Actions */}
          <div className="flex items-end gap-2">
            <Button
              type="primary"
              icon={<CloudDownloadOutlined />}
              size="large"
              loading={loading}
              onClick={handleFetchListings}
              className="bg-blue-500"
            >
              Fetch RA
            </Button>
            <Button
              icon={<SyncOutlined />}
              size="large"
              loading={comparing}
              onClick={handleCompare}
              disabled={raEvents.length === 0}
            >
              Compare
            </Button>
          </div>
        </div>

        {/* URL Preview */}
        <div className="text-sm text-gray-500">
          URL: <code className="bg-gray-100 px-2 py-1 rounded">
            https://ra.co/events/{selectedCountry}/{selectedCity}?page={currentPage}
          </code>
        </div>
      </Card>

      {/* Stats Card */}
      {comparisonResult && (
        <Card className="shadow-lg">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center p-4 bg-blue-50 rounded-lg">
              <div className="text-3xl font-bold text-blue-600">{comparisonResult.totalFromRA || 0}</div>
              <div className="text-sm text-gray-600">Total from RA</div>
            </div>
            <div className="text-center p-4 bg-green-50 rounded-lg">
              <div className="text-3xl font-bold text-green-600">{comparisonResult.inDatabase || 0}</div>
              <div className="text-sm text-gray-600">Already in DB</div>
            </div>
            <div className="text-center p-4 bg-orange-50 rounded-lg">
              <div className="text-3xl font-bold text-orange-600">{comparisonResult.missing || 0}</div>
              <div className="text-sm text-gray-600">Missing</div>
            </div>
            <div className="text-center p-4 bg-purple-50 rounded-lg">
              <div className="text-3xl font-bold text-purple-600">{selectedEvents.length}</div>
              <div className="text-sm text-gray-600">Selected</div>
            </div>
          </div>
        </Card>
      )}

      {/* Batch Actions */}
      {comparisonResult && comparisonResult.missing > 0 && (
        <Card className="shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <span className="text-sm text-gray-600">
                {selectedEvents.length} events selected for scraping
              </span>
            </div>
            <div className="flex gap-2">
              <Button
                onClick={() => setSelectedEvents([])}
                disabled={selectedEvents.length === 0}
              >
                Clear Selection
              </Button>
              <Button
                onClick={() => setSelectedEvents((comparisonResult?.missingEvents || []).map(e => e.raEventId))}
              >
                Select All Missing
              </Button>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                loading={batchScraping}
                disabled={selectedEvents.length === 0}
                onClick={handleBatchScrape}
                danger
              >
                Scrape Selected ({selectedEvents.length})
              </Button>
            </div>
          </div>

          {/* Batch Progress */}
          {batchScraping && (
            <div className="mt-4">
              <Progress
                percent={Math.round((scrapingProgress.current / scrapingProgress.total) * 100)}
                status="active"
                format={() => `${scrapingProgress.current}/${scrapingProgress.total}`}
              />
            </div>
          )}
        </Card>
      )}

      {/* Events Table */}
      <Card
        title={
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <DatabaseOutlined className="text-green-500" />
              <span>RA Events ({raEvents.length})</span>
            </div>
            <Button
              icon={<ReloadOutlined />}
              onClick={handleFetchListings}
              loading={loading}
              size="small"
            >
              Refresh
            </Button>
          </div>
        }
        className="shadow-lg"
      >
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <Spin size="large" />
          </div>
        ) : raEvents.length === 0 ? (
          <Alert
            message="No Events"
            description="Click 'Fetch RA' to load events from RA.co"
            type="info"
            showIcon
          />
        ) : (
          <Table
            columns={columns}
            dataSource={raEvents}
            rowKey="raEventId"
            pagination={false}
            size="middle"
            rowClassName={(record) => {
              if (!comparisonResult) return '';
              const isMissing = comparisonResult.missingEvents?.some(e => e.raEventId === record.raEventId);
              return isMissing ? 'bg-orange-50' : 'bg-green-50';
            }}
          />
        )}
      </Card>

      {/* Event Details Modal */}
      <Modal
        title="Event Details"
        open={detailsModalVisible}
        onCancel={() => {
          setDetailsModalVisible(false);
          setEventDetails(null);
        }}
        footer={[
          <Button key="close" onClick={() => setDetailsModalVisible(false)}>
            Close
          </Button>,
          <Button
            key="scrape"
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              if (eventDetails) {
                handleScrapeEvent({ url: eventDetails.url || eventDetails.externalLink, raEventId: eventDetails.raEventId });
                setDetailsModalVisible(false);
              }
            }}
          >
            Scrape & Add
          </Button>
        ]}
        width={800}
      >
        {loadingDetails ? (
          <div className="flex justify-center items-center py-12">
            <Spin size="large" />
          </div>
        ) : eventDetails ? (
          <div className="space-y-4">
            {eventDetails.image && (
              <div className="mb-4">
                <img
                  src={eventDetails.image}
                  alt={eventDetails.name}
                  className="w-full max-h-64 object-cover rounded-lg"
                />
              </div>
            )}
            <Descriptions bordered column={1}>
              <Descriptions.Item label="Title">{eventDetails.name}</Descriptions.Item>
              <Descriptions.Item label="Date/Time">{eventDetails.dateTime}</Descriptions.Item>
              <Descriptions.Item label="Venue">{eventDetails.location}</Descriptions.Item>
              <Descriptions.Item label="Address">{eventDetails.address}</Descriptions.Item>
              <Descriptions.Item label="Description">
                <div className="max-h-32 overflow-y-auto">
                  {eventDetails.description}
                </div>
              </Descriptions.Item>
              <Descriptions.Item label="Lineup">
                {eventDetails.lineup?.join(', ') || 'N/A'}
              </Descriptions.Item>
              <Descriptions.Item label="Genres">
                {eventDetails.genres?.map(g => (
                  <Tag key={g} color="blue">{g}</Tag>
                )) || 'N/A'}
              </Descriptions.Item>
              <Descriptions.Item label="Promoter">{eventDetails.promoter || 'N/A'}</Descriptions.Item>
            </Descriptions>
          </div>
        ) : (
          <Alert message="No details available" type="info" />
        )}
      </Modal>
    </div>
  );
};

export default RAComparatorTab;
